import { useRef, useMemo, useState, useCallback, useEffect } from "react";
import { withRouter } from "react-router-dom";

import {
  ActionToolbar,
  DeleteServiceReservationButton,
  DialogWithActions,
  GenerateServicesReservationsReport,
  GqlCalendar,
  GqlCalendarEventBuilder,
  GqlTable,
  RendererPicker,
  ServiceReservationPreview,
  ServiceReservationRender,
  ServiceReservationStatus,
  ServiceTimeSlotVariationButtonWithDialog,
} from "../../components";

import {
  Button,
  Card,
  Container,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import TableCell from "@material-ui/core/TableCell";
import EventIcon from "@material-ui/icons/Event";
import GridOnIcon from "@material-ui/icons/GridOn";

import { MUTATIONS, QUERIES } from "../../../graphql";

import {
  Service,
  ServiceReservation,
  ServiceReservationListItem,
  ServiceType,
} from "../../../graphql/models";

import useReservationsStyle from "./service_reservations.style";
import {
  ServiceReservationFilterValue,
  ServiceReservationsFilters,
} from "./service_reservations_filters";
import {
  useDateTimeUtils,
  useFormatter,
  useLoggedUserInfo,
  useSnackbarMutation,
} from "../../../hooks";
import CsiRating from "../../components/csi_rating/csi_rating";
import { EventInput } from "@fullcalendar/react";
import { ServiceReservationPrice } from "../../components/service_reservation_price";
import { queries } from "@testing-library/react";
import {
  ServiceUnavailableSlot,
  ServiceUnavailableSlotInput,
} from "../../../graphql/models/ServiceUnavailableSlot";
import { UnavailableSlot } from "../../components/unavailable_slot";
import { DeleteOutline } from "@material-ui/icons";

enum DisplayMode {
  GRID = "Tabella",
  CALENDAR = "Calendar",
}

export interface ServiceReservationsPageProps {
  service?: Service;
}

function ServiceReservationsPage(props: ServiceReservationsPageProps) {
  const { service } = props;

  const classes = useReservationsStyle();

  const { isAdminUser, isServiceProviderUser } = useLoggedUserInfo();
  const { displayDate, displayDateTime } = useDateTimeUtils();
  const { currency } = useFormatter();

  const tableRef = useRef();
  const unavailableSlotsTableRef = useRef();
  const calendarRef = useRef();

  const refetchAll = useCallback(() => {
    (tableRef as any).current?.refresh();
    (unavailableSlotsTableRef as any).current?.refresh();
    (calendarRef as any).current?.refresh();
  }, [tableRef, calendarRef, unavailableSlotsTableRef]);

  // Insert mode
  const [newSlotMode, setNewSlotMode] = useState(false);
  const [eliminaText, setEliminaText] = useState("");

  const confirmDeleteSlotRow = useMemo(
    () => "elimina" === eliminaText,
    [eliminaText]
  );

  // Display Mode: grid | calendar
  const [displayMode, setDisplayMode] = useState<DisplayMode>(
    DisplayMode.CALENDAR
  );

  const isCalendarDisplayMode = useMemo(
    () => displayMode === DisplayMode.CALENDAR,
    [displayMode]
  );

  const isGridDisplayMode = useMemo(
    () => displayMode === DisplayMode.GRID,
    [displayMode]
  );

  const [selectedReservation, setSelectedReservation] = useState<
    ServiceReservation | undefined
  >(void 0);
  const [previewDialogOpened, setPreviewDialogOpened] = useState(false);

  const openPreviewDialog = (reservation: ServiceReservation) => {
    setSelectedReservation(reservation);
    setPreviewDialogOpened(true);
  };

  const closePreviewDialog = useCallback(() => {
    setPreviewDialogOpened(false);
    setSelectedReservation(void 0);
  }, [setPreviewDialogOpened, setSelectedReservation]);

  // Dialog Actions
  const previewDialogActions = useMemo(() => {
    const actions: any[] = [];
    if (selectedReservation) {
      actions.push(
        <DeleteServiceReservationButton
          color="secondary"
          onDeleted={() => {
            setPreviewDialogOpened(false);
            refetchAll();
          }}
          serviceReservation={selectedReservation}
          disabled={Boolean(selectedReservation.deletionDate)}
        >
          ELIMINA PRENOTAZIONE
        </DeleteServiceReservationButton>
      );
    }

    actions.push(
      <Button onClick={() => closePreviewDialog()} color="primary" autoFocus>
        Chiudi
      </Button>
    );

    return actions;
  }, [closePreviewDialog, selectedReservation, refetchAll]);

  const dateNow = new Date();
  const dateFrom = new Date();
  const dateTo = new Date();

  dateFrom.setDate(dateNow.getDate() - 15);
  dateTo.setDate(dateNow.getDate() + 15);

  const [filters, setFilters] = useState<ServiceReservationFilterValue>({
    from: dateFrom,
    to: dateTo,
    serviceId: service?.id,
  });

  const calendarEventTimes = (e: EventInput, record: ServiceReservation) => {
    if (record.utcStart) {
      e.start = record.utcStart;
      e.end = record.utcEnd;
    } else {
      e.start = record.date;
      e.end = record.date + 60 * 60 * 1000;
    }
  };

  // Event builder for rendering calendar event
  const calendarEventBuilder: GqlCalendarEventBuilder = (
    record: ServiceReservation
  ) => {
    // const { from, to } = getTimeData(record);

    const { color, background } = ServiceReservationRender(
      record.status,
      record.deletionDate
    );

    const eventInput: EventInput = {
      id: record.id,
      date: record.date,
      startEditable: false,
      editable: false,
      durationEditable: false,
      title: `${record.service.name} | ${record.apartment?.code}`,
      textColor: color,
      backgroundColor: background,
    };
    calendarEventTimes(eventInput, record);
    return eventInput;
  };

  const onCalendarEventClick = (evt, clickedElement: ServiceReservation) => {
    if (clickedElement) {
      openPreviewDialog(clickedElement);
    }
  };

  const serviceReservationsSearchQueryParams = useMemo(() => {
    return {
      from: filters.from,
      to: filters.to,
      serviceId: filters.serviceId,
    };
  }, [filters]);

  const unavailableSlotsSearchQueryParams = useMemo(() => {
    return {
      from: filters.from,
      to: filters.to,
      serviceId: filters.serviceId,
    };
  }, [filters]);

  const dayHeaderContent = useMemo(() => {
    if (service && service?.type === ServiceType.LONGEVITY) {
      return (date: Date) => {
        const expireDate = new Date(date.getTime());
        expireDate.setHours(23);
        expireDate.setMinutes(59, 59);
        return (
          <ServiceTimeSlotVariationButtonWithDialog
            key={`${date.toLocaleString()}-${service.id}-dialog-button`}
            serviceId={service.id}
            date={date}
            variant="contained"
            disabled={new Date() > expireDate}
          >
            <Typography variant="caption">MODIFICA DISPONIBILITÀ</Typography>
          </ServiceTimeSlotVariationButtonWithDialog>
        );
      };
    }
  }, [service]);

  const toolbarTitle = useMemo(() => {
    if (service) {
      return `Prenotazioni ${service.name}`;
    }

    return "Prenotazioni Servizi Esterni";
  }, [service]);

  const onServiceReservationStatusUpdated = () => {
    setPreviewDialogOpened(false);
    refetchAll();
  };

  const newUnavailableSlotHandler = () => {
    setDisplayMode(DisplayMode.GRID);
    setNewSlotMode(true);
  };

  const toolbarActions = useMemo(() => {
    const actions: React.ReactNode[] = [
      <RendererPicker
        key="setDisplayMode"
        value={displayMode}
        onChange={(v) => setDisplayMode(v as DisplayMode)}
        options={[
          {
            value: DisplayMode.CALENDAR,
            renderer: <EventIcon />,
          },
          {
            value: DisplayMode.GRID,
            renderer: <GridOnIcon />,
          },
        ]}
        orientation="horizontal"
      />,
      <ServiceReservationsFilters
        key="setFilters"
        value={filters}
        setValue={setFilters}
      />,
    ];

    if (isAdminUser || isServiceProviderUser) {
      actions.push(
        <Button
          key="addNewReservation"
          variant="contained"
          color="primary"
          onClick={newUnavailableSlotHandler} // TODO agganciare il metodo per aggiungere uno slot bloccato
        >
          blocca slot
        </Button>
      );
    }

    if (isAdminUser) {
      actions.push(
        <GenerateServicesReservationsReport serviceId={service?.id} />
      );
    }

    return actions;
  }, [displayMode, filters, isAdminUser, service?.id]);

  const gqlTableColumns = useMemo(() => {
    const columns = [
      // '',
      "Servizio",
      "Codice Prenotazione",
      "Stato",
      "Data",
      "Creato Da",
      "Quando",
      "Servizi richiesti",
      "Prezzo totale",
    ];
    if (isAdminUser) {
      columns.push("Rating");
    }

    return columns;
  }, [isAdminUser]);

  const gqlUnavailableSlotsColumns = useMemo(
    () => [
      "Servizio",
      "Inizio blocco",
      "Fine blocco",
      "Numero di unità",
      "Motivo",
    ],
    []
  );

  const displayPrice = useCallback(
    (row: ServiceReservation) => {
      if (
        row.externalClientReservationData &&
        row.externalClientReservationData.details &&
        row.externalClientReservationData.details.length > 0
      ) {
        const values = row.externalClientReservationData.details.map(
          (value) => value.totalPrice ?? 0
        );
        const sum = values.reduce((total, price) => total + price);
        return <>{currency(sum / 100)}</>;
      }
      return <ServiceReservationPrice reservation={row} />;
    },
    [currency]
  );

  const listItemRenderer = useCallback((row: ServiceReservationListItem) => {
    const p = row.quantity ? `${row.quantity} x ` : "";
    return `${p}${row.label}`;
  }, []);

  const addUnavailableSlotHandler = (value: ServiceUnavailableSlotInput) => {
    refetchAll();
    setNewSlotMode(false);
  };

  const [invokeDeleteUnavailableSlot, { data: deleteUnavailableSlotData }] =
    useSnackbarMutation(MUTATIONS.DELETE_UNAVAILABLE_SLOT);

  const deleteUnavailableSlotHandler = (value: ServiceUnavailableSlot) => {
    invokeDeleteUnavailableSlot({
      variables: {
        id: value.id,
      },
    });
  };

  useEffect(() => {
    if (deleteUnavailableSlotData) refetchAll();
  }, [deleteUnavailableSlotData, refetchAll]);

  return (
    <Container className={classes.container} maxWidth={false} component="div">
      <>
        <ActionToolbar title={toolbarTitle} actions={toolbarActions} />
        {isCalendarDisplayMode && (
          <div className={classes.reservationsCalendar}>
            <GqlCalendar
              ref={calendarRef}
              query={QUERIES.SERVICE_RESERVATIONS}
              queryResultDataField="serviceReservations"
              queryVariables={serviceReservationsSearchQueryParams}
              eventBuilder={calendarEventBuilder}
              eventClick={onCalendarEventClick}
              dayHeaderContent={dayHeaderContent}
              visibleFrom={filters.from}
              visibleTo={filters.to}
              autoRefreshTimeoutInSeconds={60}
            />
          </div>
        )}
        {isGridDisplayMode && (
          <>
            {newSlotMode && (
              <>
                <Typography variant={"h5"} style={{ marginTop: "10px" }}>
                  Inserimento nuovo slot
                </Typography>
                <UnavailableSlot
                  onAdd={addUnavailableSlotHandler}
                ></UnavailableSlot>
              </>
            )}
            <Typography variant={"h5"} style={{ marginTop: "10px" }}>
              Slot bloccati
            </Typography>
            <GqlTable
              ref={unavailableSlotsTableRef}
              query={QUERIES.UNAVAILABLE_SLOTS}
              columns={gqlUnavailableSlotsColumns.map((v) => (
                <TableCell>{v}</TableCell>
              ))}
              autoRefreshTimeoutInSeconds={60}
              queryResultDataField="serviceUnavailableSlots"
              queryVariables={unavailableSlotsSearchQueryParams}
              rows={(row: ServiceUnavailableSlot) => (
                <>
                  <TableCell>{row.service?.i18n?.name_it ?? "n.d."}</TableCell>
                  <TableCell>{displayDateTime(row.blockedFrom)}</TableCell>
                  <TableCell>{displayDateTime(row.blockedTo)}</TableCell>
                  <TableCell>{row.blockedUnits}</TableCell>
                  <TableCell>{row.notes ?? "n.d."}</TableCell>
                  <TableCell>
                    <TextField
                      variant="outlined"
                      placeholder="Scrivi elimina"
                      value={eliminaText}
                      onChange={(e) => setEliminaText(e.target.value)}
                    />
                    <IconButton
                      disabled={!confirmDeleteSlotRow}
                      onClick={() => deleteUnavailableSlotHandler(row)}
                    >
                      <DeleteOutline></DeleteOutline>
                    </IconButton>
                  </TableCell>
                </>
              )}
            ></GqlTable>
            <Typography variant={"h5"} style={{ marginTop: "10px" }}>
              Prenotazioni
            </Typography>
            <GqlTable
              ref={tableRef}
              autoRefreshTimeoutInSeconds={60}
              columns={gqlTableColumns.map((v) => (
                <TableCell>{v}</TableCell>
              ))}
              query={QUERIES.SERVICE_RESERVATIONS}
              queryResultDataField="serviceReservations"
              queryVariables={serviceReservationsSearchQueryParams}
              rows={(row: ServiceReservation) => (
                <>
                  {/* <TableCell>
                  {!row.deletionDate && 
                    <DeleteButton
                      onClick={() => triggerDeleteReservation(row)}
                    />
                  }
                  {!!row.deletionDate && 
                    <strong>ANNULLATA</strong>
                  }
                </TableCell> */}
                  <TableCell>{row.service.name}</TableCell>
                  <TableCell>{row.code}</TableCell>
                  <TableCell>
                    <ServiceReservationStatus
                      serviceReservation={row}
                      status={row.status}
                      onStatusUpdated={refetchAll}
                      enableStatusChange={false}
                    />
                  </TableCell>
                  <TableCell>
                    {row.utcStart && displayDate(row.utcStart)}
                  </TableCell>
                  <TableCell>
                    {row.createdBy.name} - {row.apartment?.code}
                  </TableCell>
                  <TableCell>{row.bookingTexts?.rangeText ?? ""}</TableCell>
                  <TableCell>
                    {row.serviceTypeListItems
                      .map((v) => listItemRenderer(v))
                      .join(", ")}
                  </TableCell>
                  <TableCell>{displayPrice(row)}</TableCell>
                  {isAdminUser && (
                    <TableCell>
                      {row.customerSatisfaction && (
                        <CsiRating
                          customerSatisfaction={row.customerSatisfaction}
                        />
                      )}
                    </TableCell>
                  )}
                </>
              )}
            />
          </>
        )}
        {/* -- Popup anteprima prenotazione -- */}
        <DialogWithActions
          fullScreen={false}
          fullWidth={true}
          open={previewDialogOpened}
          setOpen={setPreviewDialogOpened}
          title={"Anteprima Prenotazione"}
          actions={previewDialogActions}
        >
          {selectedReservation && (
            <ServiceReservationPreview
              onServiceReservationStatusUpdated={
                onServiceReservationStatusUpdated
              }
              serviceReservation={selectedReservation as ServiceReservation}
            />
          )}
        </DialogWithActions>
      </>
    </Container>
  );
}

export default withRouter(ServiceReservationsPage);
